// What the audit report headline shows.
//
// The three commercial-confidence tiers (Verified / Likely / Conditional) are
// computed by the backend QA + outcomes layers and rendered unchanged in the
// tier cards. This module decides ONLY what the hero above them says. It is
// display selection — it never re-grades a finding, moves a dollar between
// tiers, or touches RULE_MONETIZATION_POLICY / applyCommercialQa / headlineImpact.
// The stored dollar_impact, value_tiers, and commercialQa blocks keep their exact
// current meaning; this changes what the report SAYS, not what the engine BELIEVES.
//
// ── POTENTIAL-FIRST REFRAME (Decision Log 2026-08-13) ────────────────────────
// Owner-facing surfaces lead with ONE headline: the bounded POTENTIAL — Verified
// + Likely + Conditional, after the exclusions below. Verified, when it exists,
// is a reinforcing second line ("including $Y you can bill today"), never a
// competing headline.
//
// Why this replaced the previous Verified+Likely rule: the engine's monetization
// registry grants headline eligibility to 14 of ~485 rules, so 95.7% of the 94
// production $0 audits were $0 by construction while averaging 16.9 findings
// each (docs/diligence/ENGINE_ACCURACY_REPORT_2026-08-13.md §1, §2). A report
// that fires 15 findings carrying $77,935 and leads with "No immediate revenue
// found" is not conservative, it is wrong about itself. "$0 confirmed" no longer
// appears anywhere, in any state: when findings exist but the bounded total is 0,
// the headline is a COUNT ("N opportunities identified — dollar amounts pending
// validation"), never a dollar sign.
//
// ── THE SANITY BOUND ─────────────────────────────────────────────────────────
// A summed potential is only publishable if it cannot read as a padding manifesto.
// Four rules, each traceable per audit via computePotential().exclusions:
//   (a) B5 QUARANTINE — the 25 rows written before Commercial QA existed on the
//       T&M path (accuracy report §3.2). Their stored dollars passed through no
//       monetization gate at all: $3,183,820 on a NULL grand total, $300,784.92
//       at 87.9% of the estimate, an $82.67-vs-$60,213.17 728x spread on one file.
//       An audit on this list contributes NOTHING and renders as pending-validation.
//   (b) HALF-ESTIMATE, PER FINDING — a single finding worth more than 50% of the
//       estimate is excluded. This is the direct fix for the 87.9% / 94.6% headlines.
//   (c) UNGRADED — a fail-closed QA error block: the QA pass could not evaluate the
//       finding at all. These already carry 0 in both amount fields, so this is
//       belt-and-braces; it exists so the exclusion trace names them rather than
//       silently passing them through. Deliberately NOT unregistered-rule dollars —
//       see isUngradedBasis() for why that test was narrowed before ship.
//   (d) HALF-ESTIMATE, AGGREGATE — (b) is per-finding, so several sub-50% findings
//       can still sum past the estimate. When the bounded total still exceeds 50%
//       of the estimate the FIGURE IS NOT CUT — the wording degrades to "over $X",
//       which reads high without asserting false precision.
// A missing or unparseable grand total (45 of 164 stored rows hold it as a STRING,
// and some are NULL) makes (b) and (d) uncomputable. That is `unbounded`, and it
// forces the "+" suffix — never a silently unbounded precise figure.
//
// ── AND THE FALSE-PRECISION DOCTRINE ─────────────────────────────────────────
// Conditional dollars are now IN the headline, which meets the doctrine in
// backend/commercial-qa.js conditionalDisplayMode() head-on. That doctrine is not
// repealed, it is bounded: a conditional aggregate is still never presented as a
// PRECISE figure unless every component independently permits one. When the
// conditional set is not precise-allowed the headline renders "$X+" and the
// Conditional tier card below stays count-only, exactly as before. The customer
// sees a floor, not a fabricated total.
//
// CommonJS so backend/verify-hero-selection.js can require() this pure logic
// without a bundler; webpack resolves the named imports in AuditReport.js.

const HERO = {
    TOTAL: 'total',       // bounded potential > 0 — the figure leads
    PENDING: 'pending',   // findings exist, bounded potential is 0 — a COUNT leads
    CLEAN: 'clean',       // no findings at all — never observed in 164 production audits
};

// Why each dollar was left out of the headline. Attached per finding in
// computePotential().exclusions so any audit's number is reconstructable.
const POTENTIAL_EXCLUSION = {
    B5_QUARANTINE: 'b5_quarantine',
    EXCEEDS_HALF_ESTIMATE: 'exceeds_half_estimate',
    UNGRADED_BASIS: 'ungraded_basis',
};

// Accuracy report §3.2 — every SOC/Invoice row written before commit 2a0b8e2
// (2026-08-03) wired Commercial QA into the T&M engine. Not a ratio check: 20 of
// these 25 produced plausible-looking numbers and would pass any ratio filter,
// which is why the list is enumerated rather than inferred. Display-layer only —
// no row is modified, hidden from History, or excluded from any stored aggregate.
// Retire this list once the B5 re-audit executes and these rows carry gated figures.
const B5_QUARANTINE_AUDIT_IDS = new Set([
    120, 146, 147, 186, 187, 188, 189, 190, 191, 192,
    198, 199, 200, 201, 202, 208, 209, 212, 213, 216,
    217, 245, 305, 306, 308,
]);

// A single finding worth more than this share of the estimate is implausible on
// its face. Also the aggregate wording threshold — see rule (d) above.
const HALF_ESTIMATE = 0.5;

// Default formatter — whole dollars. The report passes its own 2-decimal
// formatter so the subline matches the tier cards below it.
function money(n) {
    return `$${Math.round(Number(n) || 0).toLocaleString('en-US')}`;
}

// A tier only ever contributes what it actually holds: a missing, null, NaN, or
// negative total contributes zero rather than silently subtracting from the
// other tier's real money.
function tierAmount(n) {
    const v = Number(n) || 0;
    return v > 0 ? v : 0;
}

function plural(n) {
    return n === 1 ? '' : 's';
}

function opportunities(n) {
    return `opportunit${n === 1 ? 'y' : 'ies'}`;
}

// grand_total is a number in 114 stored rows and a STRING ("$998,208.34") in 45.
// Arithmetic on the string form yields NaN silently, which would open the sanity
// bound rather than close it — so it is parsed, never coerced. Returns null when
// there is no usable estimate total.
function parseEstimateTotal(v) {
    if (v === null || v === undefined) return null;
    const n = typeof v === 'number' ? v : parseFloat(String(v).replace(/[$,\s]/g, ''));
    return Number.isFinite(n) && n > 0 ? n : null;
}

// What one finding contributes to the potential. The three outcome tiers are
// mutually exclusive by construction (commercial-outcomes.classifyOutcome assigns
// an amount to exactly one of verified/likely/conditional), so this sum
// double-counts nothing. Findings with no outcome layer fall back to their QA
// block, where headlineAmount and conditionalAmount are likewise exclusive. A
// finding carrying neither block contributes NOTHING — ungated dollars never
// reach a headline.
// HEADLINE BASIS (2026-09-03). Which outcome tiers the hero figure is allowed to
// sum.
//
// Until now the hero summed all three tiers. Measured on the 158 unique Xactimate
// documents in the corpus, CONDITIONAL alone carried $5,026,521 of the $5,682,139
// total — 88.5% of every dollar Audee reports. A CONDITIONAL finding is one whose
// rule has no approved monetization policy, and its own card says so: "This rule
// has no approved monetization policy — its amount could not be established as
// commercially defensible." So the headline was asserting money that the cards
// directly beneath it disowned.
//
// The St. Louis job (a SERVPRO beta account's) is what forced this. The report headlined
// $6,034.31+ across 15 findings; the project manager reviewed every finding and
// what survived was $514 of wet insulation plus one unsized item. An estimator
// does that subtraction in about a minute, and the number is the first thing on
// the page.
//
// VERIFIED_LIKELY is the default. LIKELY means the quantity came from an actual
// line item on the estimate and only the RATE is assumed — defensible in front of
// an estimator. CONDITIONAL dollars are not deleted: they still render on their
// cards and in the "verify before counting as revenue" bucket, and the amount
// held back is returned as `conditionalHeld` so a surface can state it plainly.
//
// ALL_TIERS preserves the previous behaviour for anything that needs the old
// figure. Corpus effect of the switch: average headline $35,925 -> $4,112, and
// 38 of 158 documents fall to a count-led headline (selectHero already routes a
// zero total to HERO.PENDING, so no report leads with "$0").
const HEADLINE_BASIS = {
    VERIFIED_LIKELY: 'verified_likely',
    ALL_TIERS: 'all_tiers',
};

function basisCountsConditional(headlineBasis) {
    return headlineBasis === HEADLINE_BASIS.ALL_TIERS;
}

function findingPotentialAmount(f, headlineBasis = HEADLINE_BASIS.VERIFIED_LIKELY) {
    const countsConditional = basisCountsConditional(headlineBasis);
    const oc = f && f.commercialOutcome;
    if (oc) {
        return tierAmount(oc.verifiedAmount) + tierAmount(oc.likelyAmount)
            + (countsConditional ? tierAmount(oc.conditionalAmount) : 0);
    }
    const qa = f && f.commercialQa;
    if (qa) return tierAmount(qa.headlineAmount) + (countsConditional ? tierAmount(qa.conditionalAmount) : 0);
    return 0;
}

// True when a finding's amount is not graded well enough to sum — exclusion (c).
//
// NARROWED 2026-08-13, same day, before ship: this originally also dropped
// AMOUNT_BASIS.UNKNOWN_BASIS. But UNKNOWN_BASIS is commercial-qa's label for
// UNREGISTERED RULE, and ~93% of the engine's rules are unregistered — so that
// test withheld $208,064.80 of $496,571.41 conditional dollars (41.9%) across the
// 10 demo documents, and took audit 352's flagship $77,935 back out. It removed
// precisely the dollars the potential-first reframe exists to surface.
//
// Unregistered is not the same as ungraded. An unregistered rule still fired on
// real parsed quantities; what it lacks is a registry-approved RATE. That is a
// precision claim, and the "$X+" floor wording already carries it — such a
// finding can never make the headline read as precise, because a non-precise
// conditional set forces the suffix. So the dollars stay in and the imprecision
// is stated, rather than the figure silently collapsing.
//
// What remains excluded is the genuinely ungraded case: a fail-closed QA error
// block, where the QA pass could not evaluate the finding at all. Those already
// carry 0 in both amount fields, so this is belt-and-braces — it exists so the
// exclusion trace NAMES them rather than passing them through silently.
function isUngradedBasis(f) {
    const qa = f && f.commercialQa;
    if (!qa) return false; // no QA block contributes 0 anyway (findingPotentialAmount)
    const flags = qa.reasonablenessFlags || [];
    return flags.indexOf('commercial_qa_error_fail_closed') !== -1;
}

// ── the single source of truth for the headline figure ───────────────────────
// Both the in-app report and the audit-complete email call this; neither computes
// a bound of its own. The per-tier totals come in from the caller because each
// surface already computes them for its own tier cards (the report from its QA
// predicates, the email from rollupTiers) — passing them in is what keeps the
// headline reconciled with the cards directly beneath it, rather than being a
// fourth number nobody can tie out.
//
//   findings           the ACTIVE findings the surface is showing (post dismissal /
//                      reviewer-only filtering). Used for the exclusion trace.
//   verified/likely/conditionalTotal   that surface's own tier totals.
//   grandTotal         estimate total, number or "$1,234.56" string, or null.
//   auditId            stored audit id, or null for a fresh run (never quarantined).
//   conditionalPrecise whether every conditional component permits a precise
//                      amount (the caller's existing conditionalAmountShowable /
//                      CONDITIONAL_DISPLAY check). False → "+" suffix.
function computePotential({
    findings = [],
    verifiedTotal = 0,
    likelyTotal = 0,
    conditionalTotal = 0,
    grandTotal = null,
    auditId = null,
    conditionalPrecise = true,
    headlineBasis = HEADLINE_BASIS.VERIFIED_LIKELY,
} = {}) {
    // conditionalTotal is still accepted and still reported back as
    // `conditionalHeld` — under the default basis it is simply not summed INTO the
    // headline. Callers keep passing it because the surfaces below the hero still
    // render it.
    const countsConditional = basisCountsConditional(headlineBasis);
    const conditionalHeld = tierAmount(conditionalTotal);
    const base = tierAmount(verifiedTotal) + tierAmount(likelyTotal)
        + (countsConditional ? conditionalHeld : 0);
    const estimate = parseEstimateTotal(grandTotal);
    const unbounded = estimate === null;
    const list = Array.isArray(findings) ? findings : [];
    const exclusions = [];

    // (a) Quarantined audit — the whole row is stale, so nothing in it is summable.
    const idNum = Number(auditId);
    const quarantined = Number.isFinite(idNum) && B5_QUARANTINE_AUDIT_IDS.has(idNum);
    if (quarantined) {
        return {
            base, total: 0, excludedAmount: base, exclusions: [{
                rule_id: null, amount: base, reason: POTENTIAL_EXCLUSION.B5_QUARANTINE,
            }],
            quarantined: true, unbounded, imprecise: false, overEstimate: false,
            estimateTotal: estimate, conditionalHeld, headlineBasis,
        };
    }

    // (b) + (c) per-finding exclusions.
    let excludedAmount = 0;
    for (const f of list) {
        // Same basis as the base sum, or an excluded CONDITIONAL finding would be
        // subtracted from a total that never included it and drive the hero to $0.
        const amount = findingPotentialAmount(f, headlineBasis);
        if (amount <= 0) continue;
        let reason = null;
        if (estimate !== null && amount > estimate * HALF_ESTIMATE) {
            reason = POTENTIAL_EXCLUSION.EXCEEDS_HALF_ESTIMATE;
        } else if (isUngradedBasis(f)) {
            reason = POTENTIAL_EXCLUSION.UNGRADED_BASIS;
        }
        if (!reason) continue;
        exclusions.push({ rule_id: (f && f.rule_id) || null, amount, reason });
        excludedAmount += amount;
    }

    // Clamped: a surface's tier totals and the finding walk are computed from the
    // same data but by different predicates, so never let an exclusion push the
    // headline negative — a $0 total is the pending-validation state, not a debt.
    const total = Math.max(0, base - excludedAmount);

    // (d) aggregate bound — wording only, the figure is never cut.
    const overEstimate = estimate !== null && total > estimate * HALF_ESTIMATE;

    return {
        base,
        total,
        excludedAmount: Math.min(excludedAmount, base),
        exclusions,
        quarantined: false,
        unbounded,
        // "$X+" whenever the number is a floor rather than a settled figure: an
        // exclusion fired, there is no estimate total to bound against, or the
        // figure carries approximate money. Which money is approximate depends on
        // the basis: under ALL_TIERS it is the conditional set (hence
        // conditionalPrecise); under VERIFIED_LIKELY the conditional set is not in
        // the number at all, and what makes it a floor is LIKELY money, whose rate
        // is assumed by definition.
        imprecise: exclusions.length > 0 || unbounded
            || (countsConditional ? !conditionalPrecise : tierAmount(likelyTotal) > 0),
        overEstimate,
        estimateTotal: estimate,
        // Money deliberately NOT in the headline, so a surface can say what is
        // being held back instead of leaving the reader to find it card by card.
        conditionalHeld,
        headlineBasis,
    };
}

// How the bounded figure is written. "over $X" wins over "$X+" — it is the
// stronger hedge, and doubling them ("over $X+") reads as a typo.
function potentialAmountText(potential, fmt = money) {
    const text = fmt(potential.total);
    if (potential.overEstimate) return `over ${text}`;
    if (potential.imprecise) return `${text}+`;
    return text;
}

// ── Report sections ──────────────────────────────────────────────────────────
// Used ONLY to NAME the pending headline honestly. This never selects a tier,
// never touches a dollar, and never changes which findings render. A finding
// whose section is missing or unrecognised counts as `other`, which makes the
// homogeneity test in potentialHeadline() fail closed to the generic copy.
const SECTION = {
    REQUIRES_CORRECTION: 'Requires Correction',
    PREPARE_TO_EXPLAIN:  'Prepare to Explain',
    OPPORTUNITY:         'Opportunity',
};

function sectionCounts(findings) {
    const c = { requiresCorrection: 0, prepareToExplain: 0, opportunity: 0, other: 0 };
    for (const f of (Array.isArray(findings) ? findings : [])) {
        const s = f && f.report_section;
        if (s === SECTION.REQUIRES_CORRECTION) c.requiresCorrection++;
        else if (s === SECTION.PREPARE_TO_EXPLAIN) c.prepareToExplain++;
        else if (s === SECTION.OPPORTUNITY) c.opportunity++;
        else c.other++;
    }
    return c;
}

// WHY the pending report carries no dollar. Naming-only, like sectionCounts —
// this selects no tier, reads no registry, and touches no amount.
//
// "Pending validation" was false on the common path. Measured on the post-fix
// corpus, 397 Opportunity findings carry a null dollar_impact and 359 of them
// (90%) sit at amountBasis INFERRED_QUANTITY_ASSUMED_RATE. Nothing is pending
// inside Audee for those: the estimate never stated the quantity, so the gate
// declined to invent one. The same rate source prices every time when the
// quantity IS in the document — DIRECT_QUANTITY_UNVALIDATED_ASSUMED_RATE, 376
// findings, zero nulls. Quantity is the discriminator, not rate.
//
// So the dollar is not waiting on us; it is waiting on the reader to confirm a
// quantity. Saying that turns a passive hedge into the action the finding
// already asks for in its own exclusionReason.
//
// Returns null when the findings array is absent or carries no basis at all, so
// the caller falls through to the existing copy unchanged.
function unpricedReason(findings) {
    const list = Array.isArray(findings) ? findings : [];
    let quantity = 0, noBasis = 0, other = 0;
    for (const f of list) {
        const basis = f && f.commercialQa && f.commercialQa.amountBasis;
        if (!basis) continue;
        if (basis === 'INFERRED_QUANTITY_ASSUMED_RATE') quantity++;
        else if (basis === 'UNKNOWN_BASIS') noBasis++;
        else other++;
    }
    const seen = quantity + noBasis + other;
    if (seen === 0) return null;
    if (quantity === seen) {
        return 'Audee has not put a dollar on these — the estimate does not state the quantities they would need. Each one shows what to confirm.';
    }
    if (noBasis === seen) {
        return 'Audee has not put a dollar on these — there is no approved pricing basis for them yet. Each one shows what to confirm.';
    }
    return 'Audee has not put a dollar on these — each one shows below what it would need first.';
}

// Which headline state the surface is in. A dollar figure leads ONLY when the
// bounded potential is positive; findings with no summable dollars lead with a
// COUNT; zero findings is the clean-report state.
function selectHero({ potential = null, findingCount = 0 } = {}) {
    if (potential && potential.total > 0) return HERO.TOTAL;
    return findingCount > 0 ? HERO.PENDING : HERO.CLEAN;
}

// The full headline block, shared verbatim by the report hero and the email.
// Returns display strings only — every dollar passed in was computed by the QA +
// outcomes layers.
//
//   eyebrow      the label above the figure
//   amountText   the figure, or the count line in PENDING
//   isAmount     false in PENDING/CLEAN, so a surface can style a non-dollar line
//   note         the reinforcing second line
//   subjectSuffix  what follows "Audit Complete: <job> — " in the email subject.
//                  Never "$0" — in PENDING it describes the count instead.
function potentialHeadline({
    potential = null,
    findingCount = 0,
    verifiedTotal = 0,
    findings = [],
    fmt = money,
} = {}) {
    const p = potential || computePotential({});
    const tier = selectHero({ potential: p, findingCount });
    const n = Math.max(0, Math.trunc(Number(findingCount) || 0));
    const verified = tierAmount(verifiedTotal);

    if (tier === HERO.TOTAL) {
        const amountText = potentialAmountText(p, fmt);
        const noteParts = [`across ${n} finding${plural(n)}`];
        // Verified is a REINFORCING line, never a competing headline.
        if (verified > 0) noteParts.push(`including ${fmt(verified)} you can bill today`);
        return {
            tier,
            eyebrow: 'Potential opportunity identified',
            amountText,
            isAmount: true,
            note: noteParts.join(' · '),
            subjectSuffix: `${amountText} in potential opportunity`,
        };
    }

    if (tier === HERO.PENDING) {
        // HOMOGENEOUS PREPARE-TO-EXPLAIN. When every finding is an adjuster
        // question, two things in the generic copy below are false.
        //
        // "Opportunity" is wrong: a Prepare-to-Explain finding is something to be
        // ready to justify, not something to bill. "Pending validation" is wrong
        // too, and more damagingly — it promises a dollar that is never coming.
        // commercial-qa.js zeroes BOTH headlineAmount and conditionalAmount for
        // every finding in this section (the isAdjusterQuestion gate), by design.
        // The amount is therefore SETTLED at zero, not pending. Measured on the
        // post-fix corpus: 278 Prepare-to-Explain findings, 51 of them carrying a
        // nonzero dollar_impact, and not one reaches headline or conditional.
        //
        // Naming that correctly is the honest state, not a softer one — the
        // report stops hedging about a number it has already decided.
        //
        // Requires an EXACT, fully-classified match: any unrecognised section
        // leaves `other` positive and falls through to the generic copy.
        const sc = sectionCounts(findings);
        if (n > 0 && sc.prepareToExplain === n && sc.other === 0) {
            return {
                tier,
                eyebrow: n === 1 ? 'Item to prepare to explain' : 'Items to prepare to explain',
                amountText: `${n} item${plural(n)} to prepare to explain`,
                isAmount: false,
                note: 'Nothing here is a billing gap — these are items to be ready to justify before you send.',
                subjectSuffix: `${n} item${plural(n)} to prepare to explain`,
            };
        }

        // Findings fired but nothing survived the bound. This must never read as
        // "we checked and found nothing" — it leads with what IS there.
        //
        // The note names why there is no dollar when the findings say why;
        // absent that evidence it keeps the original line.
        return {
            tier,
            eyebrow: 'Potential opportunity identified',
            amountText: `${n} ${opportunities(n)} identified`,
            isAmount: false,
            note: unpricedReason(findings)
                || 'Dollar amounts pending validation — see what each one needs below.',
            subjectSuffix: `${n} ${opportunities(n)} identified`,
        };
    }

    // CLEAN — zero findings on a good parse. Has not occurred in 164 production
    // audits (the engine concludes documents are unpriceable, not clean), but it
    // must render honestly rather than as a $0 figure.
    return {
        tier,
        eyebrow: 'Audit complete',
        amountText: 'No findings',
        isAmount: false,
        note: 'Audee reviewed this estimate against its current rules and found nothing to flag.',
        subjectSuffix: 'no findings',
    };
}

// One line, for the foot of the exported PDF: what the headline figure is and is
// not. The PDF is the artifact most likely to leave the contractor's hands, so it
// carries its own basis statement rather than relying on the reader having seen
// the report UI.
function potentialBasisLine(potential, fmt = money) {
    const p = potential || computePotential({});
    if (!p || p.total <= 0) {
        return 'Potential opportunity is an Audee estimate of value identified for review; amounts on this report are pending validation and are not a billing statement.';
    }
    const bits = [
        // Names the basis exactly. Saying "Verified, Likely, and Conditional
        // combined" stopped being true on 2026-09-03 when Conditional left the
        // headline; a PDF footer that misstates its own arithmetic is worse than
        // no footer, because it is the line a reader checks when the number is
        // challenged.
        p.headlineBasis === HEADLINE_BASIS.ALL_TIERS
            ? `${potentialAmountText(p, fmt)} is potential opportunity identified for review — Verified, Likely, and Conditional findings combined, not an amount confirmed as billable`
            : `${potentialAmountText(p, fmt)} is potential opportunity identified for review — Verified and Likely findings only, not an amount confirmed as billable`,
    ];
    if (p.exclusions.length > 0) {
        bits.push(`${p.exclusions.length} finding${plural(p.exclusions.length)} excluded from this total as unverifiable or implausible on its face`);
    }
    if (p.overEstimate || p.unbounded) {
        bits.push('the figure is shown as a floor, not a settled amount');
    }
    return `Basis: ${bits.join('; ')}.`;
}

module.exports = {
    HERO,
    POTENTIAL_EXCLUSION,
    B5_QUARANTINE_AUDIT_IDS,
    HALF_ESTIMATE,
    HEADLINE_BASIS,
    parseEstimateTotal,
    findingPotentialAmount,
    computePotential,
    potentialAmountText,
    potentialHeadline,
    potentialBasisLine,
    selectHero,
    SECTION,
    sectionCounts,
};
